import React, { Suspense } from 'react';
import { Redirect } from 'react-router-dom';

import App from './App';
import StatsigRouteSwitch from './components/redirect/StatsigRouteSwitch';
import RouteLoading from './components/RouteLoading';
import ExternalRedirect from './components/redirect/ExternalRedirect';
import SeoLocationRedirect from './pages/SeoLocationRedirect';
import { buildJoinRoute, buildJoinSportTypeRoute, buildReferralRoute } from './components/redirect/growthPageRoutes';
import { featureGates } from './utils/statsig';
import config from '../config';
import { ReferrerTypes } from '../constants/referrals';
import withImportedEventGate from './pages/ImportedEvents/withImportedEventGate';
import { IMPORTED_TOURNAMENT_ROUTE_PATH, IMPORTED_LEAGUE_ROUTE_PATH } from './pages/ImportedEvents/importedEventPaths';

// Standard imports for frequently accessed components (alphabetized)
import ClubProfileWrapper from './pages/ClubProfiles';
import DashboardPage from './pages/DashboardPage';
import Error404 from './pages/Error404';
import Error500 from './pages/Error500';
import ErrorPage from './components/ErrorPage';
import JoinPage from './pages/JoinPage';
import LoginPage from './pages/LoginPage';
import Onboarding from './pages/Onboarding';
import MagicLinkVerificationPage from './pages/MagicLinkVerificationPage';
import ProfilePageWrapper from './pages/ProfilePage/ProfilePageWrapper';
import SearchPageWrapper from './pages/SearchPage/SearchPageWrapper';

// Import components that export { component, loadData } objects
import AppleLoginPage from './pages/LoginPage/apple-login';
import ClubsPage from './pages/ClubsPage';
import EventProfileWrapper from './pages/EventProfiles';
import FacebookLoginPage from './pages/LoginPage/facebook-login';
import GoogleLoginPage from './pages/LoginPage/google-login';
import InvoicePrintPage from './pages/InvoicePrintPage';
import MatchScorePrintPage from './pages/MatchScorePrintPage';
import MedalReportPrintPage from './pages/MedalReportPrintPage/index';
import PrintTestPage from './pages/MatchScorePrintPage/PrintTestPage';
import ProfileEditPage from './pages/ProfileEditPage';
import WebViewCollegeChart from './pages/WebViews/collegeChart';
import WebViewEventDraws from './pages/WebViews/eventDraws';
import WebViewEventPlayers from './pages/WebViews/eventPlayers';
import WebViewEventSchedule from './pages/WebViews/eventSchedule';

// These paths give a __webpack_require__ is not defined error when lazy loaded 🤷‍♂️
import ClubAddMembersPage from './pages/ClubAddMembersPage';
import ClubEditPage from './pages/ClubEditPage';
import ClubPaymentPage from './pages/ClubPaymentPage';
import CollegeCorner from './pages/CollegeCorner/CollegeCorner';
import DrawEditPage from './pages/DrawEditPage';
import EventCreatePage from './pages/EventCreatePage';
import EventEditPage from './pages/EventEditPage';
import ImpersonateMemberPage from './pages/ImpersonateMemberPage';
import Inbox from './pages/Inbox/InboxWrapper';
import ManageEventTemplatesPage from './pages/ManageEventTemplatesPage';
import ManageSavedLists from './pages/ManageSavedLists';
import MyGroups from './pages/MyGroups';
import OfferPaidHits from './pages/InfoPages/OfferPaidHits';
import PostScore from './pages/PostScorePage';
import PowerPage from './pages/PowerPage';
import PowerPerksPage from './pages/MarketingPages/PowerPerksPage';
import SettingsPage from './pages/SettingsPage';
import TennisAustraliaAuthConfirmationPage from './pages/TennisAustraliaAuthConfirmationPage';
import AppleAuthConfirmationPage from './pages/AppleAuthConfirmationPage';
import FacebookAuthConfirmationPage from './pages/FacebookAuthConfirmationPage';
import WebViewMessaging from './pages/WebViews/messaging';
import StatsigRedirect from './components/redirect/StatsigRedirect';

// Lazy loaded components for code splitting
const AccountConfirmationPage = React.lazy(() => import('./pages/OAuth/AccountConfirmationPage'));
const ActivateUtr = React.lazy(() => import('./pages/ActivateUtrPage'));
const AddClubAdminsPage = React.lazy(() => import('./pages/AddClubAdminsPage'));
const BillingPage = React.lazy(() => import('./pages/BillingPage'));
const AdditionalInfoPage = React.lazy(() => import('./pages/Onboarding/AdditionalInfoPage'));
const AustraliaRankings = React.lazy(() => import('./pages/WebViews/australiaRankings'));
const AuthorizationPage = React.lazy(() => import('./pages/OAuth/AuthorizationPage'));
const ClaimMemberPage = React.lazy(() => import('./pages/ClaimMemberPage'));
const ClubLocation = React.lazy(() => import('./pages/ClubLocationPage'));
const ClubPaymentRedirectPage = React.lazy(() => import('./pages/ClubPaymentRedirectPage'));
const CollegeCoachViewsPage = React.lazy(() => import('./pages/CollegeCoachViewsPage'));
const CollegeRecruitment = React.lazy(() => import('./pages/Onboarding/CollegeRecruitment'));
const CreateEnterpriseClubPage = React.lazy(() => import('./pages/CreateEnterpriseClubPage'));
const CreateHighSchoolClubPage = React.lazy(() => import('./pages/CreateHighSchoolClubPage'));
const CreatePaidHitPage = React.lazy(() => import('./pages/CreatePaidHitPage'));
const CreatePlayPage = React.lazy(() => import('./pages/CreatePlayPage'));
const CreatePlayersGroupPage = React.lazy(() => import('./pages/CreatePlayersGroupPage'));
const CreateTournamentClubPage = React.lazy(() => import('./pages/CreateTournamentClubPage'));
const DrawPrintPage = React.lazy(() => import('./pages/DrawPrintPage'));
const DualMatchLegacyProfilePage = React.lazy(() => import('./pages/EventProfiles/DualMatchLegacyProfilePage'));
const EmailConfirmationPage = React.lazy(() => import('./pages/emailConfirmationPage'));
const EventPostScorePage = React.lazy(() => import('./pages/PostScorePage/EventPostScorePage'));
const EventTemplateCreatePage = React.lazy(() => import('./pages/EventTemplateCreate'));
const EventTemplateEditPage = React.lazy(() => import('./pages/EventTemplateEdit'));
const EventTemplatePage = React.lazy(() => import('./pages/EventProfiles/EventTemplatePage'));
const ExplorePage = React.lazy(() => import('./pages/ExplorePage'));
const FlexLeagueReferralPage = React.lazy(() => import('./pages/FlexLeagueReferralPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const GetRatedPage = React.lazy(() => import('./pages/Onboarding/GetRatedPage'));
const GroupEventLandingPage = React.lazy(() => import('./pages/GroupInvitationLandingPage/GroupEventLandingPage'));
const GroupInvitationLandingPage = React.lazy(() => import('./pages/GroupInvitationLandingPage'));
const IconLibraryV2 = React.lazy(() => import('./pages/Examples/Design/icons'));
const ImportedLeagueMatchProfile = React.lazy(() => import('./pages/ImportedEvents/LeagueMatchProfile'));
const ImportedTournamentProfile = React.lazy(() => import('./pages/ImportedEvents/TournamentProfile'));
const Invite = React.lazy(() => import('./pages/InvitePage'));
const InviteDecline = React.lazy(() => import('./pages/InviteDeclinePage'));
const InviteErrorPage = React.lazy(() => import('./pages/ErrorInvite'));
const ManageAdultTeamTennisCaptainsPage = React.lazy(() => import('./pages/ManageAdultTeamTennisCaptainsPage'));
const ManageClubTeamRolesPage = React.lazy(() => import('./pages/ManageClubTeamRolesPage'));
const MatchSheetPage = React.lazy(() => import('./pages/MatchSheetPage'));
const MissingInfoPage = React.lazy(() => import('./pages/MissingInfoPage'));
const MultifactorCodePage = React.lazy(() => import('./pages/MultifactorCode'));
const MultifactorSetupPage = React.lazy(() => import('./pages/MultifactorSetup'));
const NCSALandingPage = React.lazy(() => import('./pages/NCSALandingPage/index'));
const OnboardingAustraliaPlayer = React.lazy(() => import('./pages/Onboarding/OnboardingAustraliaPlayerPage'));
const OnboardingSurvey = React.lazy(() => import('./pages/Onboarding/OnboardingSurveyPage'));
const OrderOfPlayPrintPage = React.lazy(() => import('./pages/OrderOfPlayPrintPage'));
const PickleballSurveyPage = React.lazy(() => import('./pages/PickleballSurveyPage'));
const PlayerGroupLandingPage = React.lazy(() => import('./pages/PlayerGroupLandingPage'));
const PostScoreSelectPage = React.lazy(() => import('./pages/PostScoreSelectPage'));
const ReferFriendsPage = React.lazy(() => import('./pages/ReferFriendsPage'));
const ReferFriendsClubPage = React.lazy(() => import('./pages/ReferFriendsClubPage'));
const R2ImportPage = React.lazy(() => import('./pages/R2ImportPage'));
const Referral = React.lazy(() => import('./pages/Referral'));
const ReferralLandingPage = React.lazy(() => import('./pages/ReferralLandingPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const SaveToList = React.lazy(() => import('./pages/SaveToList'));
const SchedulePlayEditPage = React.lazy(() => import('./pages/SchedulePlayEditPage'));
const SchedulePlayPage = React.lazy(() => import('./pages/SchedulePlayPage'));
const SecondaryEmailVerificationPage = React.lazy(() => import('./pages/SecondaryEmailVerificationPage'));
const TeamComparisionPage = React.lazy(() => import('./pages/TeamComparisionPage'));
const TargetCollegesPage = React.lazy(() => import('./pages/TargetCollegesPage'));
const CollegeCoachActivityFullListPage = React.lazy(() => import('./pages/CollegeCoachActivityFullListPage'));
const TestPage = React.lazy(() => import('./pages/TestPage'));
const UnsubscribeEmailPage = React.lazy(() => import('./pages/UnsubscribeEmailPage'));
const UpcomingEvents = React.lazy(() => import('./components/redirect/UpcomingEvents'));
const UserProfilePage = React.lazy(() => import('./pages/UserProfilePage'));
const WebViewEventProfile = React.lazy(() => import('./pages/WebViews/eventProfile'));

const getNewClubRoute = (oldRoute) => {
    switch (oldRoute) {
        case 'schools':
            return 'high-school';

        case 'colleges':
            return 'college';

        default:
            return 'club';
    }
};

// Error boundary for failed lazy loads with webpack error auto-refresh
class LazyErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, isWebpackError: false };
    }

    static getDerivedStateFromError(error) {
        console.error('Error caught by LazyErrorBoundary:', error);

        // Check if this is a webpack require error
        const isWebpackError = error?.message?.includes('__webpack_require__');

        return { hasError: true, isWebpackError };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Lazy loading error:', error, errorInfo);

        // Handle webpack errors with auto-refresh
        if (this.state.isWebpackError) {
            const refreshKey = 'webpackErrorRefreshCount';
            const currentPath = window.location.pathname;
            const pathRefreshKey = `${refreshKey}_${currentPath}`;

            // Get refresh count for this specific path
            const refreshCount = parseInt(sessionStorage.getItem(pathRefreshKey) || '0');
            const maxRefreshAttempts = 3; // Increased to 3 attempts since we're auto-refreshing

            console.warn(`Webpack error detected on ${currentPath}. Auto-refresh attempt ${refreshCount + 1}/${maxRefreshAttempts}`);

            if (refreshCount < maxRefreshAttempts) {
                // Increment refresh count for this path
                sessionStorage.setItem(pathRefreshKey, (refreshCount + 1).toString());

                // Set a timeout to clear the counter after successful navigation
                setTimeout(() => {
                    sessionStorage.removeItem(pathRefreshKey);
                }, 30000); // Clear after 30 seconds

                // Log the error for monitoring
                if (window.gtag) {
                    window.gtag('event', 'webpack_error_auto_refresh', {
                        event_category: 'error',
                        event_label: currentPath,
                        custom_parameter_1: error.message,
                        custom_parameter_2: refreshCount + 1,
                    });
                }

                // Auto-refresh immediately for webpack errors
                window.location.reload();
            } else {
                console.error(`Max refresh attempts (${maxRefreshAttempts}) reached for ${currentPath}. Showing error UI.`);

                // Log max attempts reached
                if (window.gtag) {
                    window.gtag('event', 'webpack_error_max_refresh_reached', {
                        event_category: 'error',
                        event_label: currentPath,
                        custom_parameter_1: error.message,
                    });
                }
            }
        }
    }

    render() {
        if (this.state.hasError) {
            if (this.state.isWebpackError) {
                // For webpack errors, we auto-refresh immediately in componentDidCatch
                // This render should never be reached unless max attempts exceeded
                const refreshKey = 'webpackErrorRefreshCount';
                const currentPath = window.location.pathname;
                const pathRefreshKey = `${refreshKey}_${currentPath}`;
                const refreshCount = parseInt(sessionStorage.getItem(pathRefreshKey) || '0');

                if (refreshCount < 3) {
                    // This shouldn't happen since we refresh immediately, but just in case
                    return (
                        <div className="d-flex justify-content-center align-items-center flex-column" style={{ height: '300px' }}>
                            <div className="spinner-border text-primary mb-3" role="status">
                                <span className="sr-only" data-testid="routes.webpack-error.loading-text">Loading...</span>
                            </div>
                            <h5 data-testid="routes.webpack-error.loading-title">Loading...</h5>
                        </div>
                    );
                }
            }

            return (
                <ErrorPage
                    subtitle="Something went wrong loading this page"
                    showReloadButton={true}
                    showBackButton={false}
                    onReload={() => {
                        // Clear refresh counters on manual reload
                        const currentPath = window.location.pathname;
                        const pathRefreshKey = `webpackErrorRefreshCount_${currentPath}`;
                        sessionStorage.removeItem(pathRefreshKey);
                        window.location.reload();
                    }}
                    data-testid="routes.error-page"
                />
            );
        }
        return <>{this.props.children}</>;
    }
}

// Helper function to wrap lazy components with Suspense and Error Boundary
const withSuspense = (Component) => (props) => (
    <LazyErrorBoundary>
        <Suspense fallback={<RouteLoading />}>
            <Component {...props} />
        </Suspense>
    </LazyErrorBoundary>
);

export default [
    {
        ...App,
        path: '/',
        routes: [
            {
                component: withSuspense(AuthorizationPage),
                path: '/authorize',
                notifications: {
                    disablePreRegInvitation: true,
                },
            },
            {
                component: TennisAustraliaAuthConfirmationPage,
                path: '/oauth/ta/confirm',
            },
            {
                component: AppleAuthConfirmationPage,
                path: '/oauth/apple/confirm',
            },
            {
                component: FacebookAuthConfirmationPage,
                path: '/oauth/facebook/confirm',
            },
            {
                component: withSuspense(AccountConfirmationPage),
                path: '/oauth',
                notifications: {
                    disablePreRegInvitation: true,
                },
            },
            {
                component: LoginPage,
                path: '/',
                exact: true,
                notifications: {
                    disablePreRegInvitation: true,
                },
            },
            {
                component: LoginPage,
                path: '/login',
                notifications: {
                    disablePreRegInvitation: true,
                },
            },
            {
                component: MagicLinkVerificationPage,
                path: '/magic-link',
                notifications: {
                    disablePreRegInvitation: true,
                },
            },
            // ENG-2611: same page on a second path. The iOS app claims '/magic-link*' as a
            // universal link, so a link requested from a browser-only flow is emailed here
            // instead and stays in the browser holding the member's referral cookie. Ungated
            // on purpose — a link already sitting in an inbox has to keep redeeming no matter
            // what the campaign gate says later.
            {
                component: MagicLinkVerificationPage,
                path: '/auth/magic-link',
                notifications: {
                    disablePreRegInvitation: true,
                },
            },
            // redirect from marketing team
            {
                component: () => (
                    <StatsigRedirect
                        gate={featureGates.CLUB_LINKS_REDIRECT}
                        enabledTo={`${config.nextAppHost}/club/11313`} // Next.js route
                        disabledTo={`/clubs/11313`} // legacy route
                    />
                ),
                path: '/pttamericas',
            },
            {
                component: () => (
                    <StatsigRedirect
                        gate={featureGates.CLUB_LINKS_REDIRECT}
                        enabledTo={`${config.nextAppHost}/club/12083`} // Next.js route
                        disabledTo={`/clubs/12083`}
                    />

                ),
                path: '/ptteurope',
            },
            {
                component: () => (
                    <StatsigRedirect
                        gate={featureGates.CLUB_LINKS_REDIRECT}
                        enabledTo={`${config.nextAppHost}/club/12084`} // Next.js route
                        disabledTo={`/clubs/12084`}
                    />
                ),
                path: '/pttaustralia',
            },

            // SEO
            {
                component: SeoLocationRedirect,
                path: '/countries',
            },
            {
                component: SeoLocationRedirect,
                path: '/states',
            },
            {
                component: SeoLocationRedirect,
                path: '/cities/:letter',
            },
            {
                component: SeoLocationRedirect,
                path: '/cities',
            },
            // UTR App
            {
                component: withSuspense(AdditionalInfoPage),
                path: '/additionalinfo',
                notifications: {
                    disablePreRegInvitation: true,
                },
            },
            {
                component: withSuspense(GetRatedPage),
                path: '/getrated/:step?',
                notifications: {
                    disablePreRegInvitation: true,
                },
            },
            {
                component: withSuspense(CollegeRecruitment),
                path: '/college-tennis',
            },
            {
                component: withSuspense(CollegeCoachViewsPage),
                path: '/college-coach-views/:playerId',
            },
            {
                // Pro-club self-creation was discontinued in 2019; prod has
                // redirected here for years. Now every env matches prod.
                component: () => <Redirect to={'/for-players'} />,
                path: '/create',
            },
            {
                component: withSuspense(CreateTournamentClubPage),
                path: '/create-event',
                footer: {
                    disabled: true,
                },
            },
            {
                component: withSuspense(CreateHighSchoolClubPage),
                path: '/(HSClub|highschool)',
                footer: {
                    disabled: true,
                },
            },
            {
                // Followed the discontinued pro-club wizard (/create) — same
                // destination as prod has effectively had since 2019.
                component: () => <Redirect to={'/for-players'} />,
                path: '/clubs/create',
            },
            {
                component: (props) => (
                    <StatsigRouteSwitch
                        gate={featureGates.CLUB_CREATE_ENTERPRISE_NEXT_APP}
                        enabledTo={`${config.nextAppHost}/private/clubs/create-enterprise`}
                        Component={withSuspense(CreateEnterpriseClubPage)}
                        routeProps={props}
                    />
                ),
                path: '/create-enterprise',
            },
            {
                component: withSuspense(CreatePlayersGroupPage),
                path: '/create-players-group',
            },
            {
                component: withSuspense(EmailConfirmationPage),
                path: '/confirmemail/:memberId/:token',
                notifications: {
                    disablePreRegInvitation: true,
                },
            },
            {
                component: (props) => {
                    const { clubType } = props.match.params;
                    const TeamCompare = withSuspense(TeamComparisionPage);
                    // General clubs have no teamcompare page in the new app —
                    // only /college/teamcompare and /high-school/teamcompare exist.
                    if (clubType === 'clubs') {
                        return <TeamCompare {...props} />;
                    }
                    return (
                        <StatsigRouteSwitch
                            gate={featureGates.CLUB_TEAM_COMPARE_NEXT_APP}
                            enabledTo={`${config.nextAppHost}/${getNewClubRoute(clubType)}/teamcompare`}
                            Component={TeamCompare}
                            routeProps={props}
                        />
                    );
                },
                exact: true,
                path: '/:clubType(clubs|schools|colleges)/teamcompare',
            },
            {
                // loadData
                component: withSuspense(DualMatchLegacyProfilePage),
                exact: true,
                path: '/dualmatches/:eventId',
            },
            {
                component: (props) => {
                    const { clubId, clubType } = props.match.params;
                    const newClubRoute = getNewClubRoute(clubType);
                    return (
                        <StatsigRouteSwitch
                            gate={featureGates.CLUB_EDIT_NEXT_APP}
                            enabledTo={`${config.nextAppHost}/${newClubRoute}/${clubId}/edit`}
                            Component={ClubEditPage}
                            routeProps={props}
                        />
                    );
                },
                path: '/:clubType(clubs|schools|colleges)/:clubId/edit',
            },
            {
                component: (props) => {
                    const { clubId, clubType } = props.match.params;
                    return (
                        <StatsigRouteSwitch
                            gate={featureGates.CLUB_PAYMENT_NEXT_APP}
                            enabledTo={`${config.nextAppHost}/private/${getNewClubRoute(clubType)}/${clubId}/payment`}
                            Component={ClubPaymentPage}
                            routeProps={props}
                        />
                    );
                },
                path: '/:clubType(clubs|schools|colleges)/:clubId/payment',
            },
            {
                component: withSuspense(ClubPaymentRedirectPage),
                path: '/clubs/payment',
            },
            {
                component: (props) => {
                    const { clubId, clubType } = props.match.params;
                    return (
                        <StatsigRouteSwitch
                            gate={featureGates.CLUB_POST_SCORE_NEXT_APP}
                            enabledTo={`${config.nextAppHost}/private/${getNewClubRoute(clubType)}/${clubId}/post-score`}
                            Component={PostScore}
                            routeProps={props}
                        />
                    );
                },
                path: '/:clubType(clubs|schools|colleges)/:clubId/post-score',
                header: {
                    type: 'title',
                    title: 'POST SCORES',
                },
            },
            {
                exact: true,
                path: '/clubs/:clubId',
                render: (props) => (
                    <ClubProfileWrapper
                        {...props}
                        clubType="clubs"
                    />
                ),
            },
            {
                component: (props) => {
                    const { clubId } = props.match.params;
                    return (
                        <ExternalRedirect to={`${config.nextAppHost}/high-school/${clubId}`} />
                    );
                },
                exact: true,
                path: '/schools/:clubId',
            },
            {
                component: (props) => {
                    const { clubId } = props.match.params;
                    return (
                        <ExternalRedirect to={`${config.nextAppHost}/college/${clubId}`} />
                    );
                },
                exact: true,
                path: '/colleges/:clubId',
            },
            {
                // loadData
                component: (props) => (
                    <StatsigRouteSwitch
                        gate={featureGates.COLLEGE_CORNER_NEXT_APP}
                        enabledTo={`${config.nextAppHost}/college-corner`}
                        Component={CollegeCorner}
                        routeProps={props}
                    />
                ),
                exact: true,
                path: '/college-corner',
            },
            {
                component: withSuspense(GroupInvitationLandingPage),
                path: '/groups/:groupId',
            },
            {
                component: withSuspense(GroupEventLandingPage),
                path: '/group-events/:groupEventId',
            },
            {
                // loadData
                component: (props) => (
                    <StatsigRouteSwitch
                        gate={featureGates.COLLEGE_CORNER_NEXT_APP}
                        enabledTo={`${config.nextAppHost}/college-corner/${props.match.params.page}`}
                        Component={CollegeCorner}
                        routeProps={props}
                    />
                ),
                exact: true,
                path: '/college-corner/:page(teams|players|juniors|rankings|overview)',
            },
            {
                component: (props) => {
                    const { clubId, clubType } = props.match.params;
                    const newClubRoute = getNewClubRoute(clubType);
                    return (
                        <StatsigRouteSwitch
                            gate={featureGates.CLUB_ADD_MEMBERS_NEXT_APP}
                            enabledTo={`${config.nextAppHost}/private/${newClubRoute}/${clubId}/add-members`}
                            Component={ClubAddMembersPage}
                            routeProps={props}
                        />
                    );
                },
                path: '/:clubType(clubs|schools|colleges)/:clubId/add-members',
            },
            {
                component: (props) => {
                    const { clubId } = props.match.params;
                    return (
                        <StatsigRouteSwitch
                            gate={featureGates.CLUB_ORGANIZERS_NEXT_APP}
                            enabledTo={`${config.nextAppHost}/private/${getNewClubRoute(props.match.params.clubType)}/${clubId}/organizers/add`}
                            Component={withSuspense(AddClubAdminsPage)}
                            routeProps={props}
                        />
                    );
                },
                exact: true,
                path: '/:clubType(clubs|schools|colleges)/:clubId/organizers/add',
            },
            {
                component: (props) => {
                    const { clubId } = props.match.params;
                    return (
                        <StatsigRouteSwitch
                            gate={featureGates.CLUB_TEAM_ROLES_NEXT_APP}
                            enabledTo={`${config.nextAppHost}/private/${getNewClubRoute(props.match.params.clubType)}/${clubId}/teams/roles/manage`}
                            Component={withSuspense(ManageClubTeamRolesPage)}
                            routeProps={props}
                        />
                    );
                },
                exact: true,
                path: '/:clubType(clubs|schools|colleges)/:clubId/teams/roles/manage',
            },
            {
                component: (props) => {
                    const { clubId } = props.match.params;
                    return (
                        <StatsigRouteSwitch
                            gate={featureGates.CLUB_CAPTAINS_NEXT_APP}
                            enabledTo={`${config.nextAppHost}/private/${getNewClubRoute(props.match.params.clubType)}/${clubId}/teams/captains/manage`}
                            Component={withSuspense(ManageAdultTeamTennisCaptainsPage)}
                            routeProps={props}
                        />
                    );
                },
                exact: true,
                path: '/:clubType(clubs|schools|colleges)/:clubId/teams/captains/manage',
            },
            {
                component: withSuspense(MissingInfoPage),
                path: '/events/account-info',
            },
            {
                component: EventCreatePage,
                path: '/events/create',
                header: {
                    disabled: true,
                    custom: true,
                },
            },
            {
                component: withSuspense(SchedulePlayPage),
                path: '/events/create-group-play',
                header: {
                    disabled: true,
                    custom: true,
                },
            },
            {
                component: withSuspense(SchedulePlayEditPage),
                path: '/events/group-play/:eventId/edit',
                header: {
                    disabled: true,
                    custom: true,
                },
            },
            {
                component: EventEditPage,
                path: '/events/:eventId/edit',
                header: {
                    type: 'title',
                    title: 'Edit Event',
                    disabled: true,
                },
            },
            {
                component: withSuspense(MatchSheetPage),
                path: '/events/:eventId/matchsheet',
                header: {
                    type: 'title',
                    title: 'Match Sheet Schedule',
                    disabled: true,
                },
            },
            {
                component: withSuspense(DrawPrintPage),
                path: '/events/:eventId/draws/:drawId/print',
                header: {
                    type: 'title',
                    title: 'Print Draw',
                    disabled: true,
                },
                footer: {
                    disabled: true,
                },
                supportWidget: {
                    disabled: true,
                },
                mobileWebView: true,
            },
            {
                ...MatchScorePrintPage,
                path: '/events/:eventId/draws/:drawId/print-matches',
                header: {
                    type: 'title',
                    title: 'Print Matches',
                    disabled: true,
                },
                footer: {
                    disabled: true,
                },
                supportWidget: {
                    disabled: true,
                },
            },
            {
                ...MedalReportPrintPage,
                path: '/events/:eventId/draws/:drawId/print-medal',
                header: {
                    type: 'title',
                    title: 'Print Medal Report',
                    disabled: true,
                },
                footer: {
                    disabled: true,
                },
                supportWidget: {
                    disabled: true,
                },
            },
            {
                ...PrintTestPage,
                path: '/events/print-test-page/:clubLocationFacilityName',
                header: {
                    type: 'title',
                    title: 'Print Test Page',
                    disabled: true,
                },
                footer: {
                    disabled: true,
                },
                supportWidget: {
                    disabled: true,
                },
            },
            {
                component: DrawEditPage,
                path: '/events/:eventId/draws',
                header: {
                    type: 'title',
                    title: 'Edit Draws',
                    disabled: true,
                },
                footer: {
                    disabled: true,
                },
            },
            {
                component: withSuspense(CreatePlayPage),
                path: '/events/create-play',
                header: {
                    type: 'title',
                    title: 'Create Free Play',
                    disabled: true,
                },
            },
            {
                component: withSuspense(CreatePaidHitPage),
                path: '/events/create-paid-hit',
                header: {
                    type: 'title',
                    title: 'Create Paid Hit',
                    disabled: true,
                },
            },
            {
                ...EventProfileWrapper,
                path: '/events/:eventId',
                notifications: {
                    disablePreRegInvitation: true,
                },
            },
            {
                component: withSuspense(OrderOfPlayPrintPage),
                path: '/order-of-play/print',
                header: {
                    type: 'title',
                    title: 'Print Schedule',
                    disabled: true,
                },
                footer: {
                    disabled: true,
                },
                supportWidget: {
                    disabled: true,
                },
            },
            {
                component: withSuspense(EventTemplatePage),
                path: '/event-templates/:templateId',
            },
            {
                component: () => <Redirect to={'/search?type=events'} />,
                path: '/events',
                exact: true,
            },
            {
                component: DashboardPage,
                path: '/home',
                header: {
                },
                notifications: {
                    disablePreRegInvitation: false,
                    useCustomTriggerToDisplayPreRegInvitation: true,
                },
            },
            {
                component: withSuspense(ExplorePage),
                path: '/explore',
                header: {
                },
            },
            {
                component: withSuspense(InviteErrorPage),
                path: '/inviteerror',
            },
            {
                component: withSuspense(InviteDecline),
                path: '/invite/decline',
            },
            {
                component: withSuspense(Invite),
                path: '/invite',
            },
            {
                component: buildReferralRoute(withSuspense(Referral)),
                path: '/referral/:referralCode',
            },
            {
                component: withSuspense((props) => <ReferralLandingPage {...props} referrerType={ReferrerTypes.MEMBER} />),
                path: '/referrals/member/:code',
                header: {
                    disabled: true,
                },
            },
            {
                component: withSuspense((props) => <ReferralLandingPage {...props} referrerType={ReferrerTypes.CLUB} />),
                path: '/referrals/club/:code',
                header: {
                    disabled: true,
                },
            },
            {
                component: withSuspense(ReferFriendsPage),
                path: '/refer-friends/member',
            },
            {
                component: withSuspense(ReferFriendsClubPage),
                path: '/refer-friends/club/:clubId',
            },
            {
                component: buildJoinSportTypeRoute(JoinPage),
                path: '/join/:initiatingSportType',
            },
            {
                component: buildJoinRoute(JoinPage),
                path: '/join',
            },
            {
                // loadData
                ...FacebookLoginPage,
                path: '/signin-facebook',
            },
            {
                // loadData
                ...GoogleLoginPage,
                path: '/signin-google',
            },
            {
                // loadData
                ...AppleLoginPage,
                path: '/signin-apple',
            },
            {
                component: Onboarding,
                path: '/onboarding',
                notifications: {
                    disablePreRegInvitation: true,
                },
            },
            {
                component: withSuspense(OnboardingAustraliaPlayer),
                path: '/onboarding-australia-player',
                notifications: {
                    disablePreRegInvitation: true,
                },
                // header: {
                //     disabled: true,
                //     custom: true,
                // },
            },
            {
                component: withSuspense(OnboardingSurvey),
                path: '/onboarding-survey',
                notifications: {
                    disablePreRegInvitation: true,
                },
                // header: {
                //     disabled: true,
                //     custom: true,
                // },
            },
            {
                component: withSuspense(PickleballSurveyPage),
                path: '/pickleball-survey',
                notifications: {
                    disablePreRegInvitation: true,
                },
            },
            {
                component: SearchPageWrapper,
                path: '/search',
            },
            {
                component: withSuspense(UpcomingEvents),
                path: '/upcoming-events',
            },
            {
                // component: (props) => <Redirect to={"/home?upgrade=true&loggedOutAction=join"} />,
                component: PowerPage,
                path: '/power',
            },
            {
                component: props => <Redirect to={{ pathname: `/profiles/${props.match.params.playerId}`, search: props.location.search }} status={301} />,
                exact: true,
                path: '/players/:playerId',
            },
            {
                component: props => <Redirect to={`/profiles/${props.match.params.playerId}/edit`} status={301} />,
                path: '/players/:playerId/edit',
            },
            {
                component: SettingsPage,
                path: '/settings',
            },
            {
                // loadData
                ...InvoicePrintPage,
                path: '/subscriptions/invoice/print',
                header: {
                    type: 'title',
                    title: 'Print Invoice',
                    disabled: true,
                },
            },
            {
                component: withSuspense(BillingPage),
                path: '/subscriptions',
            },
            {
                component: withSuspense(ClaimMemberPage),
                path: '/activate',
            },
            {
                component: withSuspense(ForgotPasswordPage),
                path: '/forgotpassword',
            },
            {
                component: withSuspense(ResetPasswordPage),
                path: '/resetpassword',
            },
            {
                component: ProfilePageWrapper,
                exact: true,
                path: '/profiles/:playerId',
                analyticsCategory: 'profile',
            },
            {
                component: ProfileEditPage,
                path: '/profiles/:playerId/edit',
            },
            {
                component: withSuspense(EventPostScorePage),
                path: '/post-score',
                header: {
                    type: 'title',
                    title: 'Post Score',
                    disabled: true,
                },
            },
            {
                component: withSuspense(PostScoreSelectPage),
                path: '/post-score-select',
                header: {
                    type: 'title',
                    title: 'Post Score',
                    disabled: true,
                },
            },
            {
                component: PostScore,
                path: '/player/post-score',
                header: {
                    type: 'title',
                    title: 'POST YOUR SCORE',
                },
            },
            {
                component: (props) => {
                    const { clubId } = props.match.params;
                    return (
                        <StatsigRouteSwitch
                            gate={featureGates.CLUB_LOCATIONS_NEXT_APP}
                            enabledTo={`${config.nextAppHost}/private/${getNewClubRoute(props.match.params.clubType)}/${clubId}/locations`}
                            Component={withSuspense(ClubLocation)}
                            routeProps={props}
                        />
                    );
                },
                path: '/:clubType(clubs|schools|colleges)/:clubId/locations',
            },
            {
                ...ClubsPage,
                component: (props) => (
                    <StatsigRouteSwitch
                        gate={featureGates.CLUB_MY_CLUBS_NEXT_APP}
                        enabledTo={`${config.nextAppHost}/private/clubs`}
                        Component={ClubsPage.component}
                        routeProps={props}
                    />
                ),
                path: '/clubs',
            },
            {
                component: withSuspense(SaveToList),
                path: '/saved/:listType',
            },
            {
                component: ManageSavedLists,
                path: '/save-to-list/:listType/:id/:gender',
            },
            {
                component: ManageSavedLists,
                path: '/save-to-list/:listType/:id',
            },
            {
                component: MyGroups,
                path: '/my-group',
            },
            {
                component: withSuspense(PlayerGroupLandingPage),
                path: '/player-group/:playerId',
            },
            {
                component: Inbox,
                path: '/inbox/:conversationId?',
            },
            {
                component: () => <Redirect to={'https://apps.apple.com/us/app/utr-universal-tennis-rating/id1457354053?ls=1'} />,
                path: '/mobile',
            },
            {
                component: () => <Redirect to={'https://apps.apple.com/us/app/utr-universal-tennis-rating/id1457354053?ls=1'} />,
                path: '/app',
            },
            {
                component: withSuspense(R2ImportPage),
                path: '/r2/import',
            },
            {
                component: withSuspense(SecondaryEmailVerificationPage),
                path: '/secondaryemailverification',
            },
            {
                component: withSuspense(UnsubscribeEmailPage),
                path: '/unsubscribe',
            },
            {
                component: () => (
                    <StatsigRedirect
                        gate={featureGates.CLUB_LINKS_REDIRECT}
                        enabledTo={`${config.nextAppHost}/club/8809`} // Next.js route
                        disabledTo={`/clubs/8809`} // legacy route
                    />
                ),
                path: '/tennisaustralia',
            },
            {
                component: withSuspense(TargetCollegesPage),
                path: '/my-profile/college-prospect/target-colleges',
            },
            {
                component: withSuspense(CollegeCoachActivityFullListPage),
                path: '/my-profile/college-prospect/college-coach-activity',
            },
            {
                component: withSuspense(UserProfilePage),
                path: '/profile',
            },
            {
                component: withSuspense(MultifactorSetupPage),
                path: '/mfa-setup',
            },
            {
                component: withSuspense(MultifactorCodePage),
                path: '/mfa',
            },
            {
                component: ImpersonateMemberPage,
                path: '/impersonate',
            },
            {
                component: withSuspense(EventTemplateCreatePage),
                path: '/manage-event-templates/create',
                header: {
                    disabled: true,
                    custom: true,
                },
            },
            {
                component: withSuspense(EventTemplateEditPage),
                path: '/manage-event-templates/:templateId/edit',
                header: {
                    disabled: true,
                    custom: true,
                },
            },
            {
                component: ManageEventTemplatesPage,
                path: '/manage-event-templates',
            },
            {
                component: withSuspense(NCSALandingPage),
                path: '/ncsa/register',
            },
            {
                component: OfferPaidHits,
                path: '/offer-paid-hit',
                header: {
                    type: 'title',
                    title: 'Offer Paid Hit',
                },
            },
            {
                component: withSuspense(ActivateUtr),
                path: '/activate-utr',
                header: {
                    disabled: true,
                },
                footer: {
                    disabled: true,
                },
            },
            {
                component: PowerPerksPage,
                path: '/power-perks',
                footer: {
                    disabled: true,
                },
            },
            {
                component: withSuspense(FlexLeagueReferralPage),
                path: '/flex-league-referral',
            },
            {
                component: SeoLocationRedirect,
                path: '/directory',
            },
            {
                component: WebViewMessaging,
                path: '/webview/messaging/:userId',
                notifications: {
                    disablePreRegInvitation: true,
                },
                header: {
                    disabled: true,
                },
                footer: {
                    disabled: true,
                },
                supportWidget: {
                    disabled: true,
                },
                mobileWebView: true,
            },
            {
                component: withSuspense(AustraliaRankings),
                path: '/webview/australia-rankings',
                notifications: {
                    disablePreRegInvitation: true,
                },
                supportWidget: {
                    disabled: true,
                },
                mobileWebView: true,
                header: {
                    disabled: true,
                },
                footer: {
                    disabled: true,
                },
            },
            {
                ...WebViewEventDraws,
                path: '/webview/eventDraws/:eventId', // https://localhost:3443/webview/eventDraws/34482/draw/1530f399-b50e-4a99-ba1b-5fba4b248712
                notifications: {
                    disablePreRegInvitation: true,
                },
                header: {
                    disabled: true,
                },
                footer: {
                    disabled: true,
                },
                supportWidget: {
                    disabled: true,
                },
                mobileWebView: true,
            },
            {
                ...WebViewEventPlayers,
                path: '/webview/eventPlayers/:eventId',
                notifications: {
                    disablePreRegInvitation: true,
                },
                header: {
                    disabled: true,
                },
                footer: {
                    disabled: true,
                },
                supportWidget: {
                    disabled: true,
                },
                mobileWebView: true,
            },
            {
                ...WebViewEventSchedule,
                path: '/webview/eventSchedule/:eventId',
                notifications: {
                    disablePreRegInvitation: true,
                },
                header: {
                    disabled: true,
                },
                footer: {
                    disabled: true,
                },
                supportWidget: {
                    disabled: true,
                },
                mobileWebView: true,
            },
            {
                component: withSuspense(WebViewEventProfile),
                path: '/webview/eventProfile/:eventId',
                notifications: {
                    disablePreRegInvitation: true,
                },
                header: {
                    disabled: true,
                },
                footer: {
                    disabled: true,
                },
                supportWidget: {
                    disabled: true,
                },
                mobileWebView: true,
            },
            {
                ...WebViewCollegeChart,
                path: '/webview/collegeChart/:clubId',
                notifications: {
                    disablePreRegInvitation: true,
                },
                header: {
                    disabled: true,
                },
                footer: {
                    disabled: true,
                },
                supportWidget: {
                    disabled: true,
                },
                mobileWebView: true,
            },
            {
                component: withSuspense(TestPage),
                path: '/test',
            },
            {
                component: withSuspense(IconLibraryV2),
                path: '/icons',
            },

            {
                component: withImportedEventGate(withSuspense(ImportedTournamentProfile)),
                path: IMPORTED_TOURNAMENT_ROUTE_PATH,
                exact: true,
            },
            {
                component: withImportedEventGate(withSuspense(ImportedLeagueMatchProfile)),
                path: IMPORTED_LEAGUE_ROUTE_PATH,
                exact: true,
            },
            {
                component: SeoLocationRedirect,
                path: '/:primarylocation/:secondarylocation/:tertiarylocation',
            },
            {
                component: SeoLocationRedirect,
                path: '/:primarylocation/:secondarylocation',
            },
            {
                component: SeoLocationRedirect,
                path: '/:primarylocation',
            },
            {
                component: Error500,
                path: '/500',
            },
            {
                component: Error404,
                path: '/404',
            },
            {
                component: Error404,
            },
        ],
    },
];
